import React, { useState, Fragment, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons/faQuestion';


import { Tooltip, useTooltip } from '../../Tooltip';
import { t } from '../../../helpers/t';

export function ListingRightPanel() {
  const {
    id,
    active,
    operated_by_storefront,
    price_visible,
    pricing_displays,
    readable_calculated_hourly_price,
    readable_calculated_daily_price,
    readable_calculated_weekend_price,
    readable_calculated_weekly_price,
    readable_calculated_monthly_price,
    calculated_hourly_price,
    calculated_daily_price,
    calculated_weekend_price,
    calculated_weekly_price,
    calculated_monthly_price,
    _meta: { langPrefix = '' } = {},
  } = useSelector((state) => state.listing);

  const [displayedPriceIndex, setDisplayedPriceIndex] = useState(0);
  const [prices, setPrices] = useState({});

  const estimatedTotalTitleTooltip = useTooltip();

  useEffect(() => {
    let list = [];
    const basePrice = calculated_daily_price || (calculated_weekly_price / 7);

    if (pricing_displays.hourly_price && calculated_hourly_price) {
      setDisplayedPriceIndex(1);
      list = [{
        label: 'hourly',
        price: readable_calculated_hourly_price
      }];
    }

    if (pricing_displays.daily_price && calculated_daily_price) {
      list = [...list, {
        label: 'daily',
        price: readable_calculated_daily_price
      }];
    }

    if (pricing_displays.weekend_price && calculated_weekend_price) {
      list = [...list, {
        label: 'weekend',
        price: readable_calculated_weekend_price
      }];
    }

    if (pricing_displays.weekly_price && calculated_weekly_price) {
      list = [...list, {
        label: 'weekly',
        price: readable_calculated_weekly_price,
        discount: Math.round((1 - calculated_weekly_price / 7 / basePrice) * 100),
        originalPrice: (basePrice * 7)
      }];
    }

    if (pricing_displays.monthly_price && calculated_monthly_price) {
      list = [...list, {
        label: 'monthly',
        price: readable_calculated_monthly_price,
        discount: Math.round((1 - calculated_monthly_price / 30 / basePrice) * 100),
        originalPrice: (basePrice * 30)
      }];
    }

    setPrices(list);
  }, []);

  return (
    <>
      <div className="listing-details-right mt-4" data-ng-cloak>
        {
          operated_by_storefront && (
            <div className="bg-stone-100 rounded p-4 mb-2 text-stone-900 managed-by-stf">
              <h2 className="font-normal m-0">
                {t('listings.show.sections.managedByStf.header')}
              </h2>
              <div className="text-sm" dangerouslySetInnerHTML={{ __html: t('listings.show.sections.managedByStf.information') }}/>
            </div>
          )
        }

        <div className="listing-details-panel pricing drop-shadow-lg border border-gray-300 border-solid" data-ng-class="{'price-displayed': isEstimating || hasPriceEstimate}">
          {
            price_visible && (
              <>
                <h2 data-ng-if="!hasPriceEstimate && !isEstimating" className="m-0 mb-2 text-stone-900 font-normal text-xl">
                  {t('listings.show.sections.pricing.info.placeholder')}
                </h2>

                <div data-ng-if="isEstimating" className="animate-pulse h-6 mb-2 w-48 bg-gray-200 rounded"/>

                {
                  prices.length && (
                    <div className="information pricing-preview mt-3 mb-2 flex items-end text-stone-900">
                      <div className="pricing-value">{prices[displayedPriceIndex].price}</div>
                      <Menu as="div" className="pricing-unit relative flex items-center !mb-[2px] grow bg-stone-2OO rounded">
                        <Menu.Button as="div" className="border-0 border-dashed border-b border-stone-600 cursor-pointer">
                          {t(`listings.show.sections.pricing.grain.${prices[displayedPriceIndex].label}.per`)}
                          <FontAwesomeIcon icon={faChevronDown}  className="text-[8px] ml-1" aria-hidden />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-0 top-2 z-10 mt-2 origin-bottom-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="py-1">
                              {
                                prices.map(({ label, discount }, index) => (
                                  <Menu.Item as="div" key={`pricing-list-${label}`} onClick={() => {setDisplayedPriceIndex(index)}} className="cursor-pointer">
                                    {({ active }) => (
                                      <div className={classNames(((active || displayedPriceIndex === index) ? 'bg-gray-100' : ''), 'py-2 px-4 whitespace-nowrap')}>
                                        {t(`listings.show.sections.pricing.grain.${label}.per`)}
                                        {(discount > 0) && (
                                          <span className="inline-flex items-center rounded bg-green-100 text-[10px] font-normal text-green-700 ml-2 px-1 py-0.5">
                                            -{discount}%
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </Menu.Item>
                                ))
                              }
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                      {
                        prices[displayedPriceIndex].discount > 0 && (
                          <div className="inline-flex self-center items-center rounded bg-green-100 text-sm font-medium text-green-800 mr-2 px-2">
                            -{prices[displayedPriceIndex].discount}%
                          </div>
                        )
                      }
                    </div>
                  )
                }

                <div data-ng-show="hasPriceEstimate">
                  <div className="information">
                    <div data-ng-show="hasPriceEstimate && !priceUponRequest">
                      <div className="text-gray-900 text-xl flex items-center">
                        {t('listings.show.sections.pricing.info.estimatedTotal')}
                        <FontAwesomeIcon
                          {...(estimatedTotalTitleTooltip ? { ref: estimatedTotalTitleTooltip.refs.setReference } : {})}
                          {...(estimatedTotalTitleTooltip ? estimatedTotalTitleTooltip.getReferenceProps() : {})}
                          icon={faQuestion}
                          className="text-[8px] w-3 h-3 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center ml-1 cursor-pointer self-start"
                          aria-hidden
                        />
                        {
                          estimatedTotalTitleTooltip && (
                            <Tooltip {...estimatedTotalTitleTooltip}>
                              {t('listings.show.sections.pricing.info.estimatedTotalTooltip')}
                            </Tooltip>
                          )
                        }
                      </div>
                      <div>
                        <span className="font-normal line-through text-gray-500 text-lg mr-2" data-ng-if="originalPricingTotal && originalPricingTotal > pricingTotal">
                          {'{{ originalPricingTotal / 100 | currencyFromLocale:listing.locale }}'}
                        </span>
                        <span className="pricing-value font-normal text-gray-900 text-xl">
                          {'{{ pricingTotal / 100 | currencyFromLocale:listing.locale }}'}
                        </span>
                      </div>
                      <div className="text-orange-500 text-xs" data-ng-show="hasSelectedSeasonalDates">
                        {t('listings.show.sections.pricing.info.includesSeasonalDates')}
                      </div>
                    </div>
                    <h2 data-ng-show="hasPriceEstimate && priceUponRequest" className="m-0 mb-2 text-stone-900 font-normal text-xl">
                      {t('listings.show.availableUponRequest')}
                    </h2>
                  </div>
                </div>
              </>
            )
          }

          {
            !price_visible && (
              <h2 className="m-0 mb-2 text-stone-900 font-normal text-xl">
                {t('listings.show.availableUponRequest')}
              </h2>
            )
          }

          <div className="listing-action-items">
            <div data-sf-new-form data-disabled={!active} data-form-name="inquireListing" data-lang-prefix={langPrefix}>
              {
                price_visible && (
                  <>
                    <ul className="p-0 list-none text-sm text-gray-900 mt-0" data-ng-if="hasPriceEstimate && !priceUponRequest && !isEstimating">
                      <li data-ng-repeat="line in pricingLines" className="flex border-0 border-b border-solid border-gray-100 font-extralight py-1">
                        <div className="grow items-end align-bottom inline-block">
                          <span>{'{{line.quantity}}'}</span>
                          <span className="mx-1 text-xs">x</span>
                          <span data-ng-if="line.quantity <= 1" data-translate="listings.show.sections.pricing.grain.{{line.type}}.single"/>
                          <span data-ng-if="line.quantity > 1" data-translate="listings.show.sections.pricing.grain.{{line.type}}.multiple"/>
                        </div>
                        <div className="flex items-center">
                          <div className="line-through text-gray-400 mr-1" data-ng-if="line.originalPrice && line.originalPrice > line.price">
                            {'{{line.originalPrice / 100 | currencyFromLocale:listing.locale}}'}
                          </div>
                          <div>{'{{line.price / 100 | currencyFromLocale:listing.locale}}'}</div>
                        </div>
                      </li>
                      <li data-ng-if="taxesAndFees" className="flex border-0 border-b border-solid border-gray-200 mb-1 font-extralight py-1">
                        <div className="grow items-end align-bottom inline-block flex items-center">
                          {t('listings.show.sections.pricing.info.taxAndFees')}
                        </div>
                        <div className="flex items-center">
                          <div className="line-through text-gray-400 mr-1" data-ng-if="originalTaxesAndFees && originalTaxesAndFees > taxesAndFees">
                            {'{{originalTaxesAndFees / 100 | currencyFromLocale:listing.locale}}'}
                          </div>
                          <div>{'{{taxesAndFees / 100 | currencyFromLocale:listing.locale}}'}</div>
                        </div>
                      </li>
                      <li className="flex pb-1">
                        <div className="grow items-end align-bottom inline-block flex items-center font-normal">
                          {t('listings.show.sections.pricing.info.estimatedTotal')}
                        </div>
                        <div className="flex items-center">
                          <div className="line-through text-gray-400 mr-1" data-ng-if="originalPricingTotal && originalPricingTotal > pricingTotal">
                            {'{{originalPricingTotal / 100 | currencyFromLocale:listing.locale}}'}
                          </div>
                          <div className="font-normal">{'{{pricingTotal / 100 | currencyFromLocale:listing.locale}}'}</div>
                        </div>
                      </li>
                    </ul>
                    <ul className="p-0 list-none text-sm text-gray-900"
                        data-ng-if="!priceUponRequest && isEstimating">
                      <li
                        className="flex items-center border-0 border-b border-solid border-gray-100 font-extralight pt-1 pb-2">
                        <div className="grow">
                          <div className="animate-pulse h-2 w-28 bg-gray-200 rounded"/>
                        </div>
                        <div className="animate-pulse h-2 w-24 bg-gray-200 rounded"/>
                      </li>
                      <li className="flex border-0 border-b border-solid border-gray-200 mb-1 font-extralight py-1">
                        <div className="grow items-end align-bottom inline-block">
                          {t('listings.show.sections.pricing.info.taxAndFees')}
                        </div>
                        <div className="animate-pulse h-2 w-20 bg-gray-200 rounded self-center"/>
                      </li>
                      <li className="flex font-normal pb-1">
                        <div className="grow items-end align-bottom inline-block">
                          {t('listings.show.sections.pricing.info.estimatedTotal')}
                        </div>
                        <div className="animate-pulse h-2 w-24 bg-gray-200 rounded self-center"/>
                      </li>
                    </ul>
                  </>
                )
              }
              <div data-sf-btn-submit-form id="inquire-submit" className="sf-btn-primary !py-3 !bg-emerald-500 hover:!bg-emerald-400 !text-white !font-normal">
                <div className="flex flex-col items-center justify">
                  <div className="mt-1">{t('listings.show.send_request')}</div>
                  <div className="text-xs font-extralight mt-1">{t('root.inquiry.details.titleSmallNote')}</div>
                </div>
              </div>
              <div data-ng-if="listing.response_time_average < (60 * 60 * 13)" className="flex items-center text-gray-500 justify-center mt-4">
                <FontAwesomeIcon icon={faBolt}  className="text-xs mr-1" aria-hidden />
                <span className="inquiry-message" data-sf-human-duration data-unit="seconds"
                      data-duration="listing.response_time_average < (60 * 60) ? (60 * 60) : listing.response_time_average"
                      data-content={`root.listings.${operated_by_storefront ? 'concierge' : 'owner'}.responseRate`}/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed flex px-6 py-4 bottom-0 left-0 w-full bg-white z-[90] border-0 border-t border-solid border-gray-200 lg:hidden">
        <div className="grow">
          <h2 className="m-0 mb-2 text-stone-900 font-medium">
            {t('listings.show.sections.pricing.header.price')}
          </h2>
          {
            prices.length && (
              <div className="information pricing-preview text-stone-900 font-medium">
                <div>
                  <span className="pricing-value font-normal">{prices[displayedPriceIndex].price}</span>
                  <span className="pricing-unit ml-1 font-normal text-xs">
                    {t(`listings.show.sections.pricing.grain.${prices[displayedPriceIndex].label}.per`)}
                  </span>
                </div>
              </div>
            )
          }
        </div>
        <a href={`${langPrefix}/listings/${id}/request`} target="_self" className="text-sm font-normal text-white text-center bg-emerald-500 hover:bg-emerald-400 px-4 flex flex-col items-center justify-center rounded">
          <div>{t('listings.show.send_request')}</div>
          <div className="text-xs font-extralight mb-1">{t('root.inquiry.details.titleSmallNote')}</div>
        </a>
      </div>
    </>
  );
}
